import { useCallback, useEffect, useMemo, useState } from 'react';

import { StatusPill } from '@/components/runs/StatusPill';
import { AdminSection } from '@/components/shell/AdminSection';
import { DataTable, DataTableCell, DataTableHead, DataTableLoadError, DataTableMessage, DataTableRow } from '@/components/table/DataTable';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { formatDate, formatDateTime } from '@/lib/format';

import { errorText, permissionsApi } from './permissionsApi';
import { BackLink, Field, Problem, untilText } from './shared';

/** One open request, of either kind, as the list shows it. */
export type OpenRequest = {
  kind: 'request' | 'app';
  id: string;
  who: string;
  what: string;
  reason: string;
  until?: string | null;
  createdAt: string;
};

export function mergeRequests(
  access: Awaited<ReturnType<typeof permissionsApi.decidable>>,
  apps: Awaited<ReturnType<typeof permissionsApi.decidableApps>>,
): OpenRequest[] {
  return [
    ...access.map((r) => ({ kind: 'request' as const, id: r.id, who: r.requester_label || r.requester || 'Unknown', what: `${r.integration} ${r.level}`, reason: r.reason, until: r.ends_at, createdAt: r.created_at })),
    ...apps.map((r) => ({ kind: 'app' as const, id: r.id, who: r.requester_label || 'Unknown', what: `${r.app} asks for ${r.object}`, reason: r.reason, createdAt: r.created_at })),
  ].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
}

const COLUMNS = [{ label: 'From' }, { label: 'Asks for' }, { label: 'Reason', className: 'hidden md:table-cell' }, { label: 'Asked', className: 'hidden md:table-cell' }, { label: '', className: 'text-right' }];
const keyOf = (r: OpenRequest) => `${r.kind}:${r.id}`;

export function RequestsTab({ open, onOpen }: { open?: string; onOpen: (id?: string) => void }) {
  const [rows, setRows] = useState<OpenRequest[]>();
  const [error, setError] = useState<string>();
  const [problem, setProblem] = useState<string>();
  const [busy, setBusy] = useState<string>();

  const load = useCallback(async () => {
    setError(undefined);
    try {
      // The app list may be empty or refused alone: the access list still shows.
      const [access, apps] = await Promise.all([permissionsApi.decidable(), permissionsApi.decidableApps().catch(() => [])]);
      setRows(mergeRequests(access, apps));
    } catch (failure) {
      setError(errorText(failure));
    }
  }, []);
  useEffect(() => {
    void load();
  }, [load]);

  const decide = async (row: OpenRequest, verdict: 'approve' | 'deny', reason?: string) => {
    setBusy(keyOf(row));
    setProblem(undefined);
    try {
      await permissionsApi.decide(row.kind, row.id, verdict, reason);
      if (open) onOpen();
    } catch (failure) {
      setProblem(errorText(failure)); // the service's text, for example "already closed"
    } finally {
      setBusy(undefined);
      await load();
    }
  };

  const selected = useMemo(() => (open ? rows?.find((r) => keyOf(r) === open) : undefined), [rows, open]);
  if (open && selected) return <RequestDetail row={selected} busy={busy === keyOf(selected)} problem={problem} onBack={() => onOpen()} onDecide={decide} />;

  return (
    <div className="space-y-4">
      {problem ? <Problem message={problem} /> : null}
      <DataTable>
        <DataTableHead columns={COLUMNS} />
        {error ? (
          <DataTableLoadError colSpan={COLUMNS.length} message={error} onRetry={() => void load()} />
        ) : !rows ? (
          <DataTableMessage colSpan={COLUMNS.length} loading>
            Loading requests
          </DataTableMessage>
        ) : rows.length === 0 ? (
          <DataTableMessage colSpan={COLUMNS.length}>{open ? 'This request is closed or not yours to decide.' : 'No open requests for you to decide.'}</DataTableMessage>
        ) : (
          <tbody>
            {rows.map((row) => (
              <DataTableRow key={keyOf(row)} to={`?tab=requests&open=${encodeURIComponent(keyOf(row))}`}>
                <DataTableCell>
                  <button type="button" className="font-medium text-foreground hover:underline" onClick={() => onOpen(keyOf(row))}>
                    {row.who}
                  </button>
                </DataTableCell>
                <DataTableCell>
                  {row.what} {row.kind === 'app' ? <StatusPill tone="neutral">App</StatusPill> : null}
                </DataTableCell>
                <DataTableCell className="hidden text-muted-foreground md:table-cell">{row.reason || '–'}</DataTableCell>
                <DataTableCell className="hidden text-muted-foreground md:table-cell">{formatDate(row.createdAt)}</DataTableCell>
                <DataTableCell className="text-right">
                  <DecideButtons row={row} busy={busy === keyOf(row)} onDecide={decide} />
                </DataTableCell>
              </DataTableRow>
            ))}
          </tbody>
        )}
      </DataTable>
    </div>
  );
}

export function DecideButtons({ row, busy, reason, onDecide }: { row: OpenRequest; busy: boolean; reason?: string; onDecide: (row: OpenRequest, verdict: 'approve' | 'deny', reason?: string) => void }) {
  return (
    <span className="inline-flex gap-2">
      <Button type="button" size="sm" disabled={busy} onClick={() => onDecide(row, 'approve', reason)} aria-label={`Approve ${row.who} ${row.what}`}>
        Approve
      </Button>
      <Button type="button" size="sm" variant="outline" disabled={busy} onClick={() => onDecide(row, 'deny', reason)} aria-label={`Deny ${row.who} ${row.what}`}>
        Deny
      </Button>
    </span>
  );
}

function RequestDetail({ row, busy, problem, onBack, onDecide }: { row: OpenRequest; busy: boolean; problem?: string; onBack: () => void; onDecide: (row: OpenRequest, verdict: 'approve' | 'deny', reason?: string) => void }) {
  const [reason, setReason] = useState('');
  return (
    <div className="space-y-4">
      <BackLink label="All requests" onClick={onBack} />
      {problem ? <Problem message={problem} /> : null}
      <AdminSection title={row.kind === 'app' ? 'App request' : 'Access request'} description={`Asked ${formatDateTime(row.createdAt)}`}>
        <dl className="grid gap-4 md:grid-cols-2">
          <Field label="From">{row.who}</Field>
          <Field label="Asks for">{row.what}</Field>
          <Field label="Reason">{row.reason || 'No reason given'}</Field>
          {row.kind === 'request' ? <Field label="Until">{untilText(row.until)}</Field> : null}
        </dl>
        <div className="mt-5 flex flex-wrap items-center gap-3">
          <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason for your decision (optional)" aria-label="Decision reason" className="max-w-sm" maxLength={500} />
          <DecideButtons row={row} busy={busy} reason={reason.trim() || undefined} onDecide={onDecide} />
        </div>
      </AdminSection>
    </div>
  );
}
